import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import confetti from "canvas-confetti";
import PublicLayout from "@/components/layout/PublicLayout";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import api, { BACKEND_URL } from "@/lib/api";
import {
  ArrowUpRight, ArrowRight, ArrowLeft, Calendar, Users, Trophy,
  MessagesSquare, ShoppingBag, MapPin, Mail, BarChart3, Star, Clock, UserPlus, Handshake, Bell, Newspaper, Pin, ChevronLeft, ChevronRight, ChevronDown, Facebook, Instagram, Youtube, X, Share2, Link2, Copy, Check, MessageCircle, Send, Loader2, Phone
} from "lucide-react";
import Reveal from "@/components/Reveal";
import PlayHQPreview from "@/components/PlayHQPreview";
import StatsBoard from "@/components/StatsBoard";
import { DccDuck } from "@/components/DccDuck";
import { FlyerImage } from "@/components/PlayerProfileCard";
import { Textarea } from "@/components/ui/textarea";
import { MemberApplyDialog } from "@/components/overlays/MemberApplyDialog";
import { FollowDialog } from "@/components/overlays/FollowDialog";
import { SponsorProposalDialog } from "@/components/overlays/SponsorProposalDialog";

// Time-ago short helper for news cards ("1h ago", "3d ago", "20 Aug 2026").
function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diffMs = Date.now() - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso.slice(0, 10); }
}

// Kind colour map — each news type gets a distinct muted editorial colour.
const KIND_CONFIG = {
  news:         { label: "News",         bg: "#4169E1" },
  announcement: { label: "Announcement", bg: "#E63946" },
  sponsorship:  { label: "Sponsorship",  bg: "#C4A24D" },
  player:       { label: "Player",       bg: "#2E8B57" },
  match:        { label: "Match",        bg: "#7C3AED" },
  club:         { label: "Club",         bg: "#0891B2" },
};
function getKindConfig(kind) {
  const key = (kind || "").toLowerCase().trim();
  return KIND_CONFIG[key] || KIND_CONFIG.news;
}

// Kind badge — coloured ribbon shown on the news mosaic cards.
// Accepts a ``size`` prop so it scales down neatly on compact strip cards
// where the default 10-11px "ANNOUNCEMENT" label would otherwise clip.
function NewsKindBadge({ kind, size = "md" }) {
  const cfg = getKindConfig(kind);
  const sizeClass = size === "sm"
    ? "px-1.5 py-0.5 text-[8px] tracking-[0.08em]"
    : "px-2.5 py-1 text-[10px] sm:text-[11px] tracking-[0.14em]";
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap font-bold uppercase text-white shadow-md max-w-full ${sizeClass}`}
      style={{ backgroundColor: cfg.bg, fontFamily: "'Inter', sans-serif" }}
      data-testid={`news-kind-badge-${(kind || "news").toLowerCase()}`}
    >
      {cfg.label}
    </span>
  );
}

// Straddling badge — positioned at the image/text boundary on news cards.
// Sits half inside the image area and half over the info/notes area below.
function NewsKindStraddleBadge({ kind, size = "lg", itemId }) {
  const cfg = getKindConfig(kind);
  const sizeClass = size === "sm"
    ? "px-2.5 py-[4px] text-[9px] tracking-[0.1em] left-0"
    : "px-3 py-[4px] text-[10px] sm:text-[11px] tracking-[0.14em] left-0";
  return (
    <span
      className={`absolute bottom-0 translate-y-1/2 z-10 inline-flex items-center whitespace-nowrap font-bold uppercase text-white pointer-events-none select-none shadow-[0_2px_8px_rgba(0,0,0,0.18)] ${sizeClass}`}
      style={{ backgroundColor: cfg.bg, fontFamily: "'Inter', sans-serif" }}
      data-testid={`news-type-badge-${itemId}`}
    >
      {cfg.label}
    </span>
  );
}

// Scroll indicator chevron — shows a bouncing down arrow when content overflows
function ScrollChevron() {
  const parentRef = useRef(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    const el = parentRef.current?.parentElement;
    if (!el) return;
    const check = () => {
      const overflows = el.scrollHeight > el.clientHeight + 8;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
      setShow(overflows && !atBottom);
    };
    check();
    el.addEventListener("scroll", check, { passive: true });
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => { el.removeEventListener("scroll", check); ro.disconnect(); };
  }, []);

  if (!show) return <span ref={parentRef} className="hidden" />;
  return (
    <span ref={parentRef} className="sticky bottom-1 flex justify-center w-full animate-bounce pointer-events-none z-10">
      <ChevronDown className="h-5 w-5 text-muted-foreground" />
    </span>
  );
}


// Two-column mosaic: the latest news on the left as a big featured card that
// sticks to the top of the viewport while the reader scrolls through the
// 8-item grid on the right. Once the right grid finishes, sticky releases and
// the whole page continues scrolling normally.
// On mobile the mosaic is replaced by a horizontal-scroll strip of compact
// 110px cards (matching the Upcoming events strip) with side-flanking
// chevrons for navigation.
function NewsMosaic({ items, firstEvent, onOpenEvent, getCardScale, landing }) {
  const [selected, setSelected] = useState(null);
  const scrollRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  // Track overflow so chevrons only render when there's something to swipe to.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const sync = () => {
      const maxScroll = el.scrollWidth - el.clientWidth;
      setCanLeft(el.scrollLeft > 2);
      setCanRight(el.scrollLeft < maxScroll - 2);
    };
    sync();
    el.addEventListener("scroll", sync, { passive: true });
    window.addEventListener("resize", sync);
    return () => {
      el.removeEventListener("scroll", sync);
      window.removeEventListener("resize", sync);
    };
  }, [items?.length]);
  const scrollBy = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const step = Math.max(120, Math.floor(el.clientWidth * 0.8));
    el.scrollBy({ left: dir === "left" ? -step : step, behavior: "smooth" });
  };
  if (!items || items.length === 0) return null;
  const featured = items[0];
  const rest = items.slice(1, 9); // up to 8 side cards (2 rows × 4 cols)
  const all = items.slice(0, 9);   // mobile strip renders featured + 8

  return (
    <>
      {/* Mobile feed — featured hero + vertical article list (Hurricanes-style) */}
      <div className="sm:hidden" data-testid="landing-news-mobile">
        {/* Featured hero article */}
        {featured && (
          <article
            className="cursor-pointer"
            onClick={() => setSelected(featured)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelected(featured); } }}
            data-testid={`landing-news-featured-mobile-${featured.id}`}
          >
            <div className="relative w-full aspect-[16/10] bg-[hsl(var(--secondary))] overflow-hidden">
              {featured.image_url ? (
                <img src={featured.image_url} alt={featured.title} className="w-full h-full object-contain sm:object-cover" loading="lazy" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                  <Newspaper className="h-10 w-10 opacity-50" />
                </div>
              )}
              <div className="absolute bottom-2 left-2">
                <NewsKindBadge kind={featured.kind} size="sm" />
              </div>
            </div>
            <div className="px-1 pt-3 pb-4">
              <h3 className="text-xl font-bold tracking-tight leading-snug text-foreground" style={{ fontFamily: "'Inter', sans-serif" }}>
                {featured.title}
              </h3>
              <div className="mt-1.5 text-xs text-muted-foreground">{timeAgo(featured.published_at)}</div>
            </div>
          </article>
        )}

        {/* Article list rows */}
        <div className="flex flex-col">
          {rest.map((n) => (
            <article
              key={n.id}
              className="flex gap-3 items-start border-t border-black py-3.5 cursor-pointer active:bg-muted/30 transition-colors"
              onClick={() => setSelected(n)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelected(n); } }}
              data-testid={`landing-news-row-mobile-${n.id}`}
            >
              {/* Text side */}
              <div className="flex-1 min-w-0 flex flex-col justify-between">
                <h3 className="text-[15px] font-bold leading-snug text-foreground line-clamp-3" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {n.title}
                </h3>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <NewsKindBadge kind={n.kind} size="sm" />
                  <span className="text-xs text-muted-foreground">{timeAgo(n.published_at)}</span>
                </div>
              </div>
              {/* Thumbnail */}
              {n.image_url ? (
                <div className="shrink-0 w-[clamp(72px,25vw,100px)] h-[clamp(72px,25vw,100px)] rounded-md overflow-hidden bg-[hsl(var(--secondary))]">
                  <img src={n.image_url} alt={n.title} className="w-full h-full object-contain sm:object-cover" loading="lazy" />
                </div>
              ) : (
                <div className="shrink-0 w-[clamp(72px,25vw,100px)] h-[clamp(72px,25vw,100px)] rounded-md bg-[hsl(var(--secondary))] flex items-center justify-center">
                  <Newspaper className="h-5 w-5 opacity-40" />
                </div>
              )}
            </article>
          ))}
        </div>

        {/* All News link */}
        <div className="border-t border-border/60 pt-3 pb-2 flex justify-end">
          <Link
            to="/news"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors min-h-[44px]"
            data-testid="landing-news-all-mobile"
          >
            All News <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      {/* Desktop layout — featured left, 4-col grid right */}
      <div className="hidden sm:block relative mx-5 sm:mx-6 lg:mx-[232px] overflow-hidden" data-testid="landing-news-mosaic">
        <div className="flex items-start gap-3">
          {/* Featured card */}
          <Card
            className="shrink-0 overflow-hidden rounded-sm border border-neutral-300/90 cursor-pointer transition-[transform,box-shadow] duration-200 ease-out hover:scale-[1.02] hover:-translate-y-2 hover:shadow-[0_20px_44px_-8px_rgba(0,0,0,0.22),0_8px_18px_-4px_rgba(0,0,0,0.12)] hover:z-10 flex flex-col shadow-[0_4px_16px_-4px_rgba(0,0,0,0.15),0_2px_6px_-2px_rgba(0,0,0,0.1),0_0_0_0.5px_rgba(0,0,0,0.04)]"
            style={{ width: 340 }}
            role="button"
            tabIndex={0}
            onClick={() => setSelected(featured)}
            data-testid={`landing-news-featured-${featured.id}`}
          >
            <div className="relative bg-white w-full" style={{ aspectRatio: "1080 / 1350" }}>
              {featured.image_url ? (
                <img src={featured.image_url} alt={featured.title} className="w-full h-full object-contain block" loading="lazy" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-muted-foreground bg-[hsl(var(--secondary))]"><Newspaper className="h-10 w-10 opacity-50" /></div>
              )}
              <NewsKindStraddleBadge kind={featured.kind} size="lg" itemId={featured.id} />
            </div>
            <div className="px-4 py-3 bg-[#faf8f5] shrink-0">
              <h3 className="text-base font-bold leading-tight truncate text-black" title={featured.title}>{featured.title}</h3>
              <div className="mt-1 flex items-center gap-1.5 text-xs text-black/70">
                <Calendar className="h-3 w-3 shrink-0" /><span>{(() => { try { return new Date(featured.published_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); } catch { return ""; } })()}</span>
              </div>
              {featured.summary && <p className="mt-1.5 text-sm leading-snug text-black/60 line-clamp-3">{featured.summary}</p>}
            </div>
          </Card>
          {/* Side cards — fixed size, flow naturally */}
          {rest.length > 0 && (
            <div className="flex-1 min-w-0 flex flex-wrap gap-3 content-start">
              {rest.map((n) => (
                <Card
                  key={n.id}
                  className="overflow-hidden rounded-sm border border-neutral-300/90 cursor-pointer transition-[transform,box-shadow] duration-200 ease-out hover:scale-[1.02] hover:-translate-y-2 hover:shadow-[0_20px_44px_-8px_rgba(0,0,0,0.22),0_8px_18px_-4px_rgba(0,0,0,0.12)] hover:z-10 flex flex-col shadow-[0_4px_16px_-4px_rgba(0,0,0,0.15),0_2px_6px_-2px_rgba(0,0,0,0.1),0_0_0_0.5px_rgba(0,0,0,0.04)]"
                  style={{ width: 140 }}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelected(n)}
                  data-testid={`landing-news-side-${n.id}`}
                >
                  <div className="relative bg-white w-full" style={{ aspectRatio: n.image_orientation === "landscape" ? "1200 / 630" : "1080 / 1350" }}>
                    {n.image_url ? (
                      <img src={n.image_url} alt={n.title} className="absolute inset-0 w-full h-full object-contain block" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground bg-[hsl(var(--secondary))]"><Newspaper className="h-5 w-5 opacity-50" /></div>
                    )}
                    <NewsKindStraddleBadge kind={n.kind} size="sm" itemId={n.id} />
                  </div>
                  <div className="px-2 pt-3 pb-1.5 shrink-0 bg-[#faf8f5]">
                    <h3 className="text-xs font-bold leading-tight truncate text-black" title={n.title}>{n.title}</h3>
                    <div className="mt-0.5 flex items-center gap-1 text-[9px] text-black/70">
                      <Calendar className="h-2.5 w-2.5 shrink-0" /><span>{(() => { try { return new Date(n.published_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); } catch { return ""; } })()}</span>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
      <NewsArticleDialog item={selected} onClose={() => setSelected(null)} landing={landing} />
    </>
  );
}

// Compact news card used in the mobile horizontal strip — dimensions match
// LandingEventCard so the two rails feel visually consistent.
function NewsCompactCard({ item, onOpen }) {
  return (
    <article
      className="snap-start shrink-0 w-[220px] sm:w-[220px] overflow-hidden rounded-sm border border-neutral-300/90 bg-white text-foreground cursor-pointer transition-[transform,box-shadow] duration-200 ease-out hover:scale-[1.02] hover:-translate-y-2 hover:shadow-[0_20px_44px_-8px_rgba(0,0,0,0.22),0_8px_18px_-4px_rgba(0,0,0,0.12)] hover:z-10 flex flex-col shadow-[0_4px_16px_-4px_rgba(0,0,0,0.15),0_2px_6px_-2px_rgba(0,0,0,0.1),0_0_0_0.5px_rgba(0,0,0,0.04)]"
      data-testid={`landing-news-compact-${item.id}`}
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen?.(); } }}
    >
      <div className="relative w-full aspect-[1/1] sm:aspect-[3/4] bg-[hsl(var(--secondary))]">
        {item.image_url ? (
          <img src={item.image_url} alt={item.title} className="absolute inset-0 w-full h-full object-contain" loading="lazy" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
            <Newspaper className="h-6 w-6 opacity-50" />
          </div>
        )}
        <NewsKindStraddleBadge kind={item.kind} size="sm" itemId={item.id} />
      </div>
      {/* Title / summary block — hidden on tablet+ where the featured
          grid handles article text; on phones this gives readers the
          headline without having to tap each card. The kind badge now
          lives *inside* this content flow (dynamic placement) rather
          than overlaying the image, so photos no longer get clipped. */}
      <div className="sm:hidden p-3 flex-1 min-w-0">
        <NewsKindBadge kind={item.kind} size="sm" />

        <div className="mt-1.5 text-[11px] text-muted-foreground">
          {timeAgo(item.published_at)}
        </div>
      </div>
    </article>
  );
}

// Full-article modal — mirrors the reference layout: kind badge, big
// royal-blue title, date, hero image and body text.
function NewsArticleDialog({ item, onClose, landing }) {
  const open = !!item;
  const newsImgRef = useRef(null);
  const [newsImgW, setNewsImgW] = useState(null);

  // Measure rendered image width for desktop text-width constraint
  const measureNewsImg = useCallback(() => {
    const img = newsImgRef.current;
    if (!img || window.innerWidth < 640) return;
    const elW = img.clientWidth;
    const elH = img.clientHeight;
    const natW = img.naturalWidth;
    const natH = img.naturalHeight;
    if (natW && natH && elW && elH) {
      const elAspect = elW / elH;
      const imgAspect = natW / natH;
      // object-contain: actual displayed width
      const displayedW = imgAspect > elAspect ? elW : Math.round(elH * imgAspect);
      setNewsImgW(displayedW);
    } else {
      setNewsImgW(img.getBoundingClientRect().width);
    }
  }, []);
  useEffect(() => {
    window.addEventListener("resize", measureNewsImg);
    return () => window.removeEventListener("resize", measureNewsImg);
  }, [measureNewsImg]);
  // Reset when dialog opens/closes
  useEffect(() => { if (!item) setNewsImgW(null); }, [item]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="p-0 w-[98vw] max-w-[98vw] sm:w-auto sm:max-w-[80vw] max-h-[98dvh] overflow-hidden rounded-xl border-0 shadow-2xl bg-white text-foreground [&>button]:hidden"
        data-testid="news-article-dialog"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{item?.title || "News article"}</DialogTitle>
        </DialogHeader>
        {item && (
          <>
            <DialogClose
              className="absolute top-2 right-2 z-50 p-1 bg-white/80 backdrop-blur-sm rounded-full sm:hidden"
              data-testid="news-article-close"
              aria-label="Close"
            >
              <X className="h-7 w-7 text-black" strokeWidth={3} />
            </DialogClose>

            <div className="flex flex-col max-h-[98dvh] sm:max-h-[95dvh] p-2 sm:p-4 cursor-pointer" onClick={() => onClose()}>
              {item.image_url ? (
                item.source_kind === "team_selection" && item.player_hotspots?.length > 0 ? (
                  <FlyerImage
                    item={item}
                    imgRef={newsImgRef}
                    onLoad={measureNewsImg}
                    className="max-w-full max-h-[60dvh] object-contain rounded-lg sm:mx-auto sm:block sm:max-h-[75dvh]"
                  />
                ) : (
                  <img
                    ref={newsImgRef}
                    onLoad={measureNewsImg}
                    src={item.image_url}
                    alt={item.title}
                    className="max-w-full max-h-[60dvh] object-contain rounded-lg sm:mx-auto sm:block sm:max-h-[75dvh]"
                    data-testid="news-article-image"
                  />
                )
              ) : (
                <div className="flex items-center justify-center h-[min(200px,30dvh)] text-muted-foreground">
                  <Newspaper className="h-16 w-16 opacity-50" />
                </div>
              )}

              {/* Text details — scrollable on both phone and desktop */}
              <div
                className="relative flex-shrink-0 sm:flex-shrink overflow-y-auto scrollbar-hide sm:max-h-[30dvh]"
                style={newsImgW ? { maxWidth: newsImgW, marginInline: "auto" } : undefined}
              >
                <div className="p-3 sm:p-4 space-y-2.5">
                <NewsKindBadge kind={item.kind} />
                <h1
                  className="text-xl sm:text-2xl font-bold tracking-tight leading-tight"
                  style={{ fontFamily: "'Inter', sans-serif", color: "#000000" }}
                  data-testid="news-article-title"
                >
                  {item.title}
                </h1>
                <div className="text-sm sm:text-sm text-muted-foreground">
                  {item.published_at
                    ? new Date(item.published_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
                    : ""}
                </div>
                {item.summary && (
                  <p className="text-sm sm:text-sm leading-relaxed text-foreground/80" data-testid="news-article-summary">
                    {item.summary}
                  </p>
                )}
                <NewsShareSheet item={item} />
                </div>
                <ScrollChevron />
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function NewsFeaturedCard({ item, onOpen, cardKey, scaleOverride }) {
  const isLandscape = item.image_orientation === "landscape";
  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); } catch { return ""; }
  };
  // Landscape: 16:9 ratio (604×340) matching event cards
  // When landscape, match portrait card height so position 1 looks consistent
  const portraitImageH = Math.round(340 * (1350 / 1080)); // 425
  const baseW = isLandscape ? 604 : 340;
  const baseH = undefined;
  const w = scaleOverride?.w || baseW;
  const h = scaleOverride?.h || baseH;
  const scaleRatio = w / baseW;
  const cardH = isLandscape ? Math.round((portraitImageH + 86) * scaleRatio) : h;
  return (
    <Card
      className="snap-start shrink-0 overflow-hidden rounded-sm border border-neutral-300/90 cursor-pointer transition-[transform,box-shadow] duration-200 ease-out hover:scale-[1.02] hover:-translate-y-2 hover:shadow-[0_20px_44px_-8px_rgba(0,0,0,0.22),0_8px_18px_-4px_rgba(0,0,0,0.12)] hover:z-10 flex flex-col shadow-[0_4px_16px_-4px_rgba(0,0,0,0.15),0_2px_6px_-2px_rgba(0,0,0,0.1),0_0_0_0.5px_rgba(0,0,0,0.04)]"
      style={{ width: w, ...(cardH ? { height: cardH } : h ? { height: h } : {}) }}
      data-testid={`landing-news-featured-${item.id}`}
      data-landing-card={cardKey}
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen?.(); } }}
    >
      <div className="relative bg-white w-full" style={isLandscape ? { height: Math.round(340 * scaleRatio) } : { aspectRatio: "1080 / 1350" }}>
        {item.image_url ? (
          isLandscape ? (
            <img src={item.image_url} alt={item.title} className="absolute top-1/2 left-1/2 object-contain block" style={{ width: w, height: Math.round(340 * scaleRatio), transform: "translate(-50%, -50%)" }} loading="lazy" />
          ) : (
            <img src={item.image_url} alt={item.title} className="w-full h-full object-contain block" loading="lazy" />
          )
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground bg-[hsl(var(--secondary))]">
            <Newspaper className="h-6 w-6 sm:h-10 sm:w-10 opacity-50" />
          </div>
        )}
        <NewsKindStraddleBadge kind={item.kind} size="lg" itemId={item.id} />
      </div>
      <div className={`px-3 sm:px-4 py-2.5 sm:py-3 bg-[#faf8f5] ${isLandscape ? "flex-1 flex flex-col justify-center" : "shrink-0"}`} style={isLandscape ? { height: Math.round(86 * scaleRatio) } : {}} data-testid={`landing-news-featured-info-${item.id}`}>
        <h3 className="text-sm sm:text-base font-bold leading-tight truncate text-black" title={item.title}>
          {item.title}
        </h3>
        <div className="mt-1 flex items-center gap-1.5 text-[11px] sm:text-xs text-black/70">
          <Calendar className="h-3 w-3 shrink-0" />
          <span>{fmtDate(item.published_at)}</span>
        </div>
        {item.summary && (
          <p className="mt-1.5 text-xs sm:text-sm leading-snug text-black/60 line-clamp-3">{item.summary}</p>
        )}
      </div>
    </Card>
  );
}

function NewsSideCard({ item, onOpen, cardKey, scaleOverride }) {
  const isLandscape = item.image_orientation === "landscape";
  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); } catch { return ""; }
  };
  const baseW = 212.5;
  const w = scaleOverride?.w || baseW;

  return (
    <Card
      className={`overflow-hidden rounded-sm border border-neutral-300/90 cursor-pointer transition-[transform,box-shadow] duration-200 ease-out hover:scale-[1.02] hover:-translate-y-2 hover:shadow-[0_20px_44px_-8px_rgba(0,0,0,0.22),0_8px_18px_-4px_rgba(0,0,0,0.12)] hover:z-10 flex flex-col shadow-[0_4px_16px_-4px_rgba(0,0,0,0.15),0_2px_6px_-2px_rgba(0,0,0,0.1),0_0_0_0.5px_rgba(0,0,0,0.04)]`}
      style={{ width: w }}
      data-testid={`landing-news-side-${item.id}`}
      data-landing-card={cardKey}
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen?.(); } }}
    >
      <div className="relative bg-white w-full" style={{ aspectRatio: isLandscape ? "1200 / 630" : "1080 / 1350" }}>
        {item.image_url ? (
          <img src={item.image_url} alt={item.title} className="absolute inset-0 w-full h-full object-contain block" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground bg-[hsl(var(--secondary))]">
            <Newspaper className="h-5 w-5 opacity-50" />
          </div>
        )}
        <NewsKindStraddleBadge kind={item.kind} size="sm" itemId={item.id} />
      </div>
      <div className="px-2 pt-3.5 pb-1.5 shrink-0 bg-[#faf8f5]">
        <h3 className="text-xs font-bold leading-tight truncate text-black" title={item.title}>
          {item.title}
        </h3>
        <div className="mt-0.5 flex items-center gap-1 text-[9px] text-black/70">
          <Calendar className="h-2.5 w-2.5 shrink-0" />
          <span>{fmtDate(item.published_at)}</span>
        </div>
      </div>
    </Card>
  );
}

// Returns true when an event has started but not yet ended.
function isHappeningNow(ev) {
  if (!ev.start_at || !ev.end_at) return false;
  const now = new Date();
  return new Date(ev.start_at) <= now && now < new Date(ev.end_at);
}

// Format an event date range for the detail dialog.
function fmtEventDateRange(start, end) {
  if (!start) return "";
  try {
    const s = new Date(start);
    const sStr = s.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });
    if (!end) return sStr;
    const e = new Date(end);
    // Same day — only show end time
    if (s.toDateString() === e.toDateString()) {
      return `${sStr} – ${e.toLocaleString(undefined, { hour: "numeric", minute: "2-digit" })}`;
    }
    return `${sStr} – ${e.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}`;
  } catch { return start; }
}

// Format price from cents to display string.
function fmtPrice(cents) {
  const n = Number(cents) || 0;
  if (n <= 0) return null;
  return `$${(n / 100).toLocaleString(undefined, { minimumFractionDigits: n % 100 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`;
}

// Card used in the public landing "Upcoming events" horizontal strip.
// Clicking opens a detail dialog with the full event information.
function LandingEventCard({ ev, onOpen, cardKey, scaleOverride }) {
  const happeningNow = isHappeningNow(ev);
  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
    } catch { return ""; }
  };
  const fmtTime = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    } catch { return ""; }
  };
  const isLandscape = ev.image_orientation === "landscape";
  // Responsive card widths:
  // Mobile (<640px): 72vw portrait / 80vw landscape — shows peek of next card
  // Desktop: original fixed widths
  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;
  const baseW = isLandscape ? 604 : 340;
  const mobileW = isLandscape ? Math.min(Math.round(window.innerWidth * 0.8), 380) : Math.min(Math.round(window.innerWidth * 0.72), 300);
  const baseH = undefined;
  const w = scaleOverride?.w || (isMobile ? mobileW : baseW);
  const h = scaleOverride?.h || baseH;
  const scaleRatio = w / baseW;
  return (
    <Card
      className={`snap-start shrink-0 overflow-hidden rounded-sm border border-neutral-300/90 cursor-pointer transition-[transform,box-shadow] duration-200 ease-out hover:scale-[1.02] hover:-translate-y-2 hover:shadow-[0_20px_44px_-8px_rgba(0,0,0,0.22),0_8px_18px_-4px_rgba(0,0,0,0.12)] hover:z-10 flex flex-col shadow-[0_4px_16px_-4px_rgba(0,0,0,0.15),0_2px_6px_-2px_rgba(0,0,0,0.1),0_0_0_0.5px_rgba(0,0,0,0.04)] ${isLandscape ? "self-center" : "self-start"}`}
      style={{ width: w, maxWidth: "calc(100vw - 40px)", ...(h ? { height: h } : {}) }}
      data-testid={`landing-event-card-${ev.id}`}
      data-landing-card={cardKey}
      onClick={() => onOpen && onOpen(ev)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && onOpen) { e.preventDefault(); onOpen(ev); } }}
    >
      <div className="relative bg-white overflow-hidden" style={isLandscape ? { height: Math.round(340 * scaleRatio) } : { aspectRatio: "1080 / 1350" }}>
        {ev.pinned_on_landing && (
          <div
            className="absolute top-1.5 left-1.5 sm:top-2.5 sm:left-2.5 z-10 inline-flex items-center gap-0.5 sm:gap-1 rounded-full bg-black/70 backdrop-blur-sm text-white text-[9px] sm:text-[10px] font-semibold uppercase tracking-[0.1em] sm:tracking-[0.14em] px-1.5 sm:px-2.5 py-0.5 sm:py-1 shadow-md"
            data-testid={`landing-event-pinned-${ev.id}`}
            title={ev.pin_expires_at ? `Pinned until ${new Date(ev.pin_expires_at).toLocaleString(undefined, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}` : "Pinned"}
          >
            <Pin className="h-2.5 w-2.5 sm:h-3 sm:w-3" fill="currentColor" /> Pinned
          </div>
        )}
        {happeningNow && (
          <div
            className="absolute top-1.5 right-1.5 sm:top-2.5 sm:right-2.5 z-10 inline-flex items-center gap-1 sm:gap-1.5 rounded-full bg-red-600/90 backdrop-blur-sm text-white text-[9px] sm:text-[10px] font-semibold uppercase tracking-[0.1em] sm:tracking-[0.14em] px-1.5 sm:px-2.5 py-0.5 sm:py-1 shadow-md"
            data-testid={`landing-event-live-${ev.id}`}
          >
            <span className="relative flex h-2 w-2 sm:h-2.5 sm:w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
              <span className="relative inline-flex rounded-full h-full w-full bg-white" />
            </span>
            Happening now
          </div>
        )}
        {ev.image_url ? (
          <img
            src={ev.image_url}
            alt={ev.title}
            className={isLandscape
              ? "w-full h-full object-cover block"
              : "w-full h-full object-cover block"}
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground bg-[hsl(var(--secondary))]">
            <Calendar className="h-6 w-6 sm:h-10 sm:w-10 opacity-50" />
          </div>
        )}
      </div>
      <div className="px-3 sm:px-4 py-2.5 sm:py-3 shrink-0 bg-[#faf8f5]" data-testid={`landing-event-info-${ev.id}`}>
        <h3 className="text-sm sm:text-base font-bold leading-tight truncate text-black" title={ev.title}>
          {ev.title}
        </h3>
        <div className="mt-1 flex items-center gap-1.5 text-[11px] sm:text-xs text-black/70">
          <Calendar className="h-3 w-3 shrink-0" />
          <span>
            {fmtDate(ev.start_at)}{ev.start_at ? `, ${fmtTime(ev.start_at)}` : ""}
            {ev.end_at ? ` — ${fmtDate(ev.end_at)}, ${fmtTime(ev.end_at)}` : ""}
          </span>
        </div>
        {ev.location && (
          <div className="mt-0.5 flex items-center gap-1.5 text-[11px] sm:text-xs text-black/60">
            <MapPin className="h-3 w-3 shrink-0" />
            <span className="truncate">{ev.location}</span>
          </div>
        )}
      </div>
    </Card>
  );
}


// --- Share sheet for event detail modal ---
// On mobile (navigator.share supported): triggers native OS share sheet.
// On desktop / fallback: shows an inline dropdown with Copy Link, Facebook, X, Email, SMS.
function EventShareSheet({ ev }) {
  const [copied, setCopied] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  if (!ev) return null;

  const shareUrl = `${window.location.origin}/api/share/event/${ev.id}`;
  const shareTitle = ev.title || "Check out this event";
  const shareText = ev.description
    ? `${shareTitle} — ${ev.description.slice(0, 100)}${ev.description.length > 100 ? "…" : ""}`
    : shareTitle;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = shareUrl;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(shareTitle);
  const encodedText = encodeURIComponent(shareText);

  const channels = [
    {
      id: "copy",
      label: copied ? "Copied!" : "Copy link",
      icon: copied ? Check : Link2,
      action: () => { copyLink(); },
      highlight: copied,
    },
    {
      id: "whatsapp",
      label: "WhatsApp",
      icon: MessageCircle,
      href: `https://wa.me/?text=${encodedText}%0A%0A${encodedUrl}`,
      color: "text-[#25D366]",
    },
    {
      id: "facebook",
      label: "Facebook",
      icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      color: "text-[#1877F2]",
    },
    {
      id: "instagram",
      label: "Instagram",
      icon: Instagram,
      action: async () => { await copyLink(); const { toast } = await import("sonner"); toast.info("Link copied — paste it in your Instagram story or DM"); },
      color: "text-[#E4405F]",
    },
    {
      id: "x",
      label: "X",
      icon: X,
      href: `https://x.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    },
    {
      id: "email",
      label: "Email",
      icon: Mail,
      href: `mailto:?subject=${encodedTitle}&body=${encodedText}%0A%0A${encodedUrl}`,
    },
  ];

  // Same compact grid on all devices
  return (
    <div className="pt-1" ref={menuRef}>
      <Button
        variant="outline"
        size="sm"
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground min-h-[44px]"
        data-testid="event-detail-share-btn"
        onClick={() => setMenuOpen((v) => !v)}
      >
        <Share2 className="h-4 w-4" /> Share event
      </Button>
      {menuOpen && (
        <div
          className="mt-2 rounded-lg border border-border bg-popover p-1.5 shadow-lg"
          data-testid="event-share-popover"
        >
          <div className="grid grid-cols-3 gap-1">
            {channels.map((ch) => {
              const Icon = ch.icon;
              const cls = `flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${ch.highlight ? "text-emerald-600 font-medium" : ch.color || "text-foreground"}`;
              if (ch.action) {
                return (
                  <button
                    key={ch.id}
                    onClick={ch.action}
                    data-testid={`share-option-${ch.id}`}
                    className={cls}
                  >
                    <Icon className="h-3.5 w-3.5 shrink-0" />
                    {ch.label}
                  </button>
                );
              }
              return (
                <a
                  key={ch.id}
                  href={ch.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`share-option-${ch.id}`}
                  className={cls}
                  onClick={() => setMenuOpen(false)}
                >
                  <Icon className="h-3.5 w-3.5 shrink-0" />
                  {ch.label}
                </a>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


// --- Share sheet for news articles (mirrors EventShareSheet) ---
function NewsShareSheet({ item }) {
  const [copied, setCopied] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  if (!item) return null;

  const shareUrl = `${window.location.origin}/api/share/news/${item.id}`;
  const shareTitle = item.title || "Check out this article";
  const shareText = item.summary
    ? `${shareTitle} — ${item.summary.slice(0, 100)}${item.summary.length > 100 ? "…" : ""}`
    : shareTitle;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = shareUrl;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(shareTitle);
  const encodedText = encodeURIComponent(shareText);

  const channels = [
    {
      id: "copy",
      label: copied ? "Copied!" : "Copy link",
      icon: copied ? Check : Link2,
      action: () => { copyLink(); },
      highlight: copied,
    },
    {
      id: "whatsapp",
      label: "WhatsApp",
      icon: MessageCircle,
      href: `https://wa.me/?text=${encodedText}%0A%0A${encodedUrl}`,
      color: "text-[#25D366]",
    },
    {
      id: "facebook",
      label: "Facebook",
      icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      color: "text-[#1877F2]",
    },
    {
      id: "instagram",
      label: "Instagram",
      icon: Instagram,
      action: async () => { await copyLink(); const { toast } = await import("sonner"); toast.info("Link copied — paste it in your Instagram story or DM"); },
      color: "text-[#E4405F]",
    },
    {
      id: "x",
      label: "X",
      icon: X,
      href: `https://x.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    },
    {
      id: "email",
      label: "Email",
      icon: Mail,
      href: `mailto:?subject=${encodedTitle}&body=${encodedText}%0A%0A${encodedUrl}`,
    },
  ];

  return (
    <div className="pt-1" ref={menuRef}>
      <Button
        variant="outline"
        size="sm"
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground min-h-[44px]"
        data-testid="news-detail-share-btn"
        onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
      >
        <Share2 className="h-4 w-4" /> Share article
      </Button>
      {menuOpen && (
        <div
          className="mt-2 rounded-lg border border-border bg-popover p-1.5 shadow-lg"
          data-testid="news-share-popover"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="grid grid-cols-3 gap-1">
            {channels.map((ch) => {
              const Icon = ch.icon;
              const cls = `flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${ch.highlight ? "text-emerald-600 font-medium" : ch.color || "text-foreground"}`;
              if (ch.action) {
                return (
                  <button
                    key={ch.id}
                    onClick={ch.action}
                    data-testid={`news-share-option-${ch.id}`}
                    className={cls}
                  >
                    <Icon className="h-3.5 w-3.5 shrink-0" />
                    {ch.label}
                  </button>
                );
              }
              return (
                <a
                  key={ch.id}
                  href={ch.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`news-share-option-${ch.id}`}
                  className={cls}
                  onClick={() => setMenuOpen(false)}
                >
                  <Icon className="h-3.5 w-3.5 shrink-0" />
                  {ch.label}
                </a>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


// Full-detail event dialog — shows poster, date range, location,
// description, price, and optional CTA link.
// Includes mobile swipe-down-to-close with smooth slide animation.
function EventDetailDialog({ ev, onClose, landing }) {
  const open = !!ev;
  const happeningNow = ev ? isHappeningNow(ev) : false;
  const price = ev ? fmtPrice(ev.price_cents) : null;
  const evImgRef = useRef(null);
  const [evImgW, setEvImgW] = useState(null);

  // Measure rendered image width for desktop text-width constraint
  const measureEvImg = useCallback(() => {
    const img = evImgRef.current;
    if (!img || window.innerWidth < 640) return;
    const elW = img.clientWidth;
    const elH = img.clientHeight;
    const natW = img.naturalWidth;
    const natH = img.naturalHeight;
    if (natW && natH && elW && elH) {
      const elAspect = elW / elH;
      const imgAspect = natW / natH;
      // object-contain: actual displayed width
      const displayedW = imgAspect > elAspect ? elW : Math.round(elH * imgAspect);
      setEvImgW(displayedW);
    } else {
      setEvImgW(img.getBoundingClientRect().width);
    }
  }, []);
  useEffect(() => {
    window.addEventListener("resize", measureEvImg);
    return () => window.removeEventListener("resize", measureEvImg);
  }, [measureEvImg]);
  // Reset when dialog opens/closes
  useEffect(() => { if (!ev) setEvImgW(null); }, [ev]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        className="p-0 w-[98vw] max-w-[98vw] sm:w-auto sm:max-w-[80vw] max-h-[98dvh] overflow-hidden rounded-xl border-0 shadow-2xl bg-white text-foreground [&>button]:hidden"
        data-testid="event-detail-dialog"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{ev?.title || "Event"}</DialogTitle>
        </DialogHeader>
        {ev && (
          <>
            <DialogClose
              className="absolute top-2 right-2 z-50 p-1 bg-white/80 backdrop-blur-sm rounded-full sm:hidden"
              data-testid="event-detail-close"
              aria-label="Close"
            >
              <X className="h-7 w-7 text-black" strokeWidth={3} />
            </DialogClose>

            <div className="flex flex-col max-h-[98dvh] sm:max-h-[95dvh] p-2 sm:p-4 cursor-pointer" onClick={() => onClose()}>
              {ev.image_url && (
                <img
                  ref={evImgRef}
                  onLoad={measureEvImg}
                  src={ev.image_url}
                  alt={ev.title}
                  className="max-w-full max-h-[60dvh] sm:max-h-[60dvh] object-contain rounded-lg sm:mx-auto sm:block"
                  data-testid="event-detail-image"
                />
              )}

              {/* Text details — scrollable on both phone and desktop */}
              <div
                className="relative flex-shrink-0 sm:flex-shrink overflow-y-auto scrollbar-hide sm:max-h-[30dvh]"
                style={evImgW ? { maxWidth: evImgW, marginInline: "auto" } : undefined}
              >
                <div className="p-3 sm:p-4 space-y-2.5">
                {happeningNow && (
                  <div className="inline-flex items-center gap-1.5 rounded-full bg-red-600/90 text-white text-xs sm:text-[10px] font-semibold uppercase tracking-[0.14em] px-2.5 py-1">
                    <span className="relative flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                      <span className="relative inline-flex rounded-full h-full w-full bg-white" />
                    </span>
                    Happening now
                  </div>
                )}

                <h1
                  className="text-xl sm:text-2xl font-bold tracking-tight leading-tight"
                  style={{ fontFamily: "'Inter', sans-serif", color: "#000000" }}
                  data-testid="event-detail-title"
                >
                  {ev.title}
                </h1>

                <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm sm:text-sm text-muted-foreground">
                  <div className="inline-flex items-center gap-1.5">
                    <Clock className="h-4 w-4 sm:h-3.5 sm:w-3.5 text-[hsl(var(--accent))]" />
                    {fmtEventDateRange(ev.start_at, ev.end_at)}
                  </div>
                  {ev.location && (
                    <div className="inline-flex items-center gap-1.5">
                      <MapPin className="h-4 w-4 sm:h-3.5 sm:w-3.5 text-[hsl(var(--accent))]" />
                      {ev.location}
                    </div>
                  )}
                  {price && (
                    <div className="inline-flex items-center gap-1.5 font-semibold text-foreground">
                      {price} <span className="font-normal text-muted-foreground text-sm">AUD</span>
                    </div>
                  )}
                </div>

                {ev.description && (
                  <p className="text-sm sm:text-sm text-foreground/80 leading-relaxed" data-testid="event-detail-description">
                    {ev.description}
                  </p>
                )}

                <div className="flex items-center gap-3 pt-1" onClick={(e) => e.stopPropagation()}>
                  {ev.cta_url && (
                    <a
                      href={ev.cta_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] px-5 py-2 min-h-[44px] text-sm font-semibold hover:opacity-90 transition-opacity"
                      data-testid="event-detail-cta"
                    >
                      {ev.cta_label || "Learn more"} <ArrowUpRight className="h-4 w-4" />
                    </a>
                  )}
                  <EventShareSheet ev={ev} />
                </div>
                </div>
                <ScrollChevron />
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// --- Sponsor tier badge config ---
// Maps recognised tier labels (case-insensitive) to display name + muted editorial colors.
// Unrecognised or missing tiers produce null → no badge rendered.
const TIER_BADGES = {
  gold:   { label: "Gold",   bg: "bg-[#C4A24D]/90", text: "text-[#5C4A1E]", border: "border-[#C4A24D]/30" },
  silver: { label: "Silver", bg: "bg-[#8E929A]/85", text: "text-[#2A2D31]", border: "border-[#8E929A]/30" },
  bronze: { label: "Bronze", bg: "bg-[#A87B56]/85", text: "text-[#4A3422]", border: "border-[#A87B56]/30" },
};
function getTierBadge(tier) {
  if (!tier) return null;
  const key = tier.trim().toLowerCase();
  return TIER_BADGES[key] || null;
}

// Rotating marquee strip of sponsor logos, shown above the Upcoming
// events section. Duplicates the list twice so the CSS marquee scroll
// loops seamlessly; pauses on hover so visitors can read a logo before
// clicking through. Each logo is a link that opens the sponsor site in a
// new tab.
function SponsorShowcase({ sponsors, selectedId, setSelectedId, landing, getCardScale, marqueePaused }) {
  // Hooks must be called before any early returns
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [sponsorClosing, setSponsorClosing] = useState(false);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const lastSponsorRef = useRef(null);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const closeSponsor = useCallback(() => {
    setSponsorClosing(true);
    setTimeout(() => {
      setSelectedId(null);
      setSponsorClosing(false);
    }, 200);
  }, [setSelectedId]);

  if (!sponsors || sponsors.length === 0) return null;
  if (landing?.sponsors_section_visible === false) return null;
  // Duplicate the list so the 33.33% marquee shift loops without a jump.
  const track = [...sponsors, ...sponsors, ...sponsors];
  // Fire-and-forget click tracker.
  const trackClick = (id) => {
    try { api.post(`/sponsors/${id}/click`).catch(() => {}); } catch {}
  };

  const selected = selectedId ? sponsors.find((s) => s.id === selectedId) : null;

  // Smooth close animation — keep overlay mounted during fade-out
  if (selected) lastSponsorRef.current = selected;
  const displaySponsor = sponsorClosing ? lastSponsorRef.current : selected;
  const showOverlay = !!displaySponsor;

  return (
    <section className="border-b border-border" data-testid="landing-sponsors-section">
      {/* Marquee viewport — full-bleed, edge-to-edge; logos slide continuously with no side fade. */}
      <div
        className="relative overflow-hidden w-full"
        style={{ maxWidth: "1920px", height: "clamp(100px, 30vw, 138px)", margin: "0 auto" }}
        data-testid="landing-sponsors-marquee"
      >
        <div
          className={`anim-sponsor-marquee flex items-center gap-8 sm:gap-12 w-max h-full bg-white ${selectedId || marqueePaused ? "marquee-paused" : ""}`}
          style={{ animationDuration: `${landing?.sponsors_carousel_speed ?? 40}s` }}
        >
            {track.map((s, i) => {
              const realIdx = i % sponsors.length;
              const scale = getCardScale?.(`sponsors_${realIdx}`);
              const sw = scale?.w || 208;
              const sh = scale?.h || 110;
              return (
              <button
                key={`${s.id}-${i}`}
                type="button"
                onClick={() => { trackClick(s.id); setSelectedId(selectedId === s.id ? null : s.id); }}
                className={`shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent))] cursor-pointer ${selectedId === s.id ? "ring-2 ring-emerald-500" : ""}`}
                aria-label={`View sponsor ${s.name}`}
                data-testid={`landing-sponsor-${s.id}`}
                data-landing-card={`sponsors_${realIdx}`}
              >
                <div className="relative flex items-center justify-center rounded-sm overflow-hidden transition-transform duration-200 ease-out hover:scale-[1.05] hover:z-10" style={{ width: sw, height: sh }}>
                  {s.logo_url ? (
                    <img
                      src={s.logo_url}
                      alt={s.name}
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <div className="text-xs font-semibold text-foreground text-center px-2 line-clamp-2">{s.name}</div>
                  )}
                  {(() => {
                    const badge = getTierBadge(s.tier);
                    if (!badge) return null;
                    return (
                      <span
                        className={`absolute top-1 left-1 px-1.5 py-[1px] rounded-sm text-[8px] font-bold uppercase tracking-wider leading-tight border ${badge.bg} ${badge.text} ${badge.border} pointer-events-none select-none`}
                        data-testid={`sponsor-tier-badge-${s.id}`}
                      >
                        {badge.label}
                      </span>
                    );
                  })()}
                </div>
              </button>
              );
            })}
        </div>
      </div>

      {/* Expanded sponsor detail — centred overlay */}
      {showOverlay && (
        <>
          <div
            className={`fixed inset-0 z-40 bg-black/50 backdrop-blur-sm transition-opacity duration-200 ease-out ${sponsorClosing ? 'opacity-0' : 'opacity-100'}`}
            onClick={closeSponsor}
            data-testid="sponsor-detail-backdrop"
          />
          <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-200 ease-out ${sponsorClosing ? 'opacity-0' : 'opacity-100'}`} onClick={closeSponsor}>
            <div
              className={`bg-background rounded-xl shadow-2xl border border-border max-w-lg w-full max-h-[90vh] overflow-y-auto scrollbar-hide p-6 sm:p-8 relative cursor-pointer transition-all duration-200 ease-out ${sponsorClosing ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}
              onClick={(e) => { if (window.innerWidth >= 640) e.stopPropagation(); }}
              data-testid={`sponsor-detail-${displaySponsor.id}`}
            >
              <button
                type="button"
                onClick={closeSponsor}
                className="absolute top-3 right-3 h-8 w-8 flex items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors sm:hidden"
                aria-label="Close"
                data-testid="sponsor-detail-close"
              >
                <X className="h-5 w-5" />
              </button>
              {/* Logo top centre */}
              {displaySponsor.logo_url && (
                <div className="flex justify-center mb-4">
                  <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-lg bg-white border border-border flex items-center justify-center p-1 overflow-hidden">
                    <img src={displaySponsor.logo_url} alt={displaySponsor.name} className="max-h-full max-w-full object-contain" />
                  </div>
                </div>
              )}
              <div>
                <div className="flex items-center justify-center gap-2 flex-wrap">
                  <div className="font-bold text-lg sm:text-xl">{displaySponsor.name}</div>
                    {(() => {
                      const badge = getTierBadge(displaySponsor.tier);
                      if (!badge) return null;
                      return (
                        <span
                          className={`inline-flex px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider leading-tight border ${badge.bg} ${badge.text} ${badge.border}`}
                          data-testid={`sponsor-detail-tier-badge-${displaySponsor.id}`}
                        >
                          {badge.label}
                        </span>
                      );
                    })()}
                  </div>
                  {displaySponsor.website_url && (
                    <a
                      href={displaySponsor.website_url}
                      target="_blank"
                      rel="noreferrer noopener sponsored"
                      className="text-sm text-[#4169E1] hover:underline break-all"
                      data-testid={`sponsor-website-${displaySponsor.id}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {displaySponsor.website_url}
                    </a>
                  )}
                  {displaySponsor.description && (
                    <p className="mt-3 text-sm text-muted-foreground whitespace-pre-wrap">{displaySponsor.description}</p>
                  )}
                  {!displaySponsor.description && !displaySponsor.website_url && (
                    <p className="mt-2 text-sm text-muted-foreground italic">No details available for this sponsor.</p>
                  )}
                </div>
                <ScrollChevron />
            </div>
          </div>
        </>
      )}
    </section>
  );
}

// Small pill used in the Season Stats section header
function StatChip({ label, value, tone, testid }) {
  const toneCls =
    tone === "success" ? "bg-emerald-600 text-white border-transparent" :
    tone === "danger" ? "bg-red-600 text-white border-transparent" :
    "bg-background text-foreground border-border";
  return (
    <div
      className={`px-3 py-1.5 rounded-full border text-xs font-semibold inline-flex items-center gap-1.5 ${toneCls}`}
      data-testid={testid}
    >
      <span className="opacity-70">{label}</span>
      <span className="tabular-nums text-sm">{value}</span>
    </div>
  );
}


// Hero image that uses phone-specific position values on mobile (<640px)
function HeroImage({ landing }) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== "undefined" && window.innerWidth < 640);
  const [livePos, setLivePos] = useState(null);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Listen for live hero-image-preview from editor iframe
  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === "hero-image-preview") {
        setLivePos({ x: e.data.x, y: e.data.y, scale: e.data.scale });
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const x = livePos?.x ?? (isMobile ? (landing.hero_image_x_phone ?? landing.hero_image_x ?? 50) : (landing.hero_image_x ?? 50));
  const y = livePos?.y ?? (isMobile ? (landing.hero_image_y_phone ?? landing.hero_image_y ?? 50) : (landing.hero_image_y ?? 50));
  const scale = livePos?.scale ?? (isMobile ? (landing.hero_image_scale_phone ?? landing.hero_image_scale ?? 1) : (landing.hero_image_scale ?? 1));

  return (
    <img
      src={landing.hero_image_url}
      alt="Hero"
      loading="lazy"
      decoding="async"
      fetchPriority="high"
      className="absolute inset-0 w-full h-full"
      style={{
        objectFit: "cover",
        objectPosition: isMobile ? `calc(${x}% - clamp(40px, 25vw, 100px)) ${y}%` : `${x}% ${y}%`,
        transform: `scale(${scale})`,
        transformOrigin: `${x}% ${y}%`,
      }}
      data-testid="landing-hero-image"
    />
  );
}


// Responsive hero overlay: picks phone/tablet/desktop overlay values based on viewport width
// Listens for postMessage from the editor for live drag preview
function HeroOverlay({ landing, setShowMemberApply, setShowSponsorProposal, featureLocks, isPrimaryAdmin }) {
  const [vw, setVw] = useState(() => (typeof window !== "undefined" ? window.innerWidth : 1920));
  const [liveOverride, setLiveOverride] = useState(null);

  useEffect(() => {
    const onResize = () => setVw(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Listen for live overlay updates from the editor iframe parent
  useEffect(() => {
    const handler = (e) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type === "overlay-preview") {
        setLiveOverride(e.data.overlay);
      }
      if (e.data?.type === "overlay-preview-clear") {
        setLiveOverride(null);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  // If live override from editor drag, use it directly
  let ox, oy, os;
  if (liveOverride) {
    ox = liveOverride.x ?? 50;
    oy = liveOverride.y ?? 50;
    os = liveOverride.scale ?? 1;
  } else if (vw < 640) {
    ox = landing?.hero_overlay_x_phone ?? landing?.hero_overlay_x ?? 50;
    oy = landing?.hero_overlay_y_phone ?? landing?.hero_overlay_y ?? 50;
    os = landing?.hero_overlay_scale_phone ?? landing?.hero_overlay_scale ?? 1;
  } else if (vw < 1024) {
    ox = landing?.hero_overlay_x_tablet ?? landing?.hero_overlay_x ?? 50;
    oy = landing?.hero_overlay_y_tablet ?? landing?.hero_overlay_y ?? 50;
    os = landing?.hero_overlay_scale_tablet ?? landing?.hero_overlay_scale ?? 1;
  } else {
    ox = landing?.hero_overlay_x ?? 50;
    oy = landing?.hero_overlay_y ?? 50;
    os = landing?.hero_overlay_scale ?? 1;
  }
  return (
    <>
      <div
        className="absolute z-10 w-full sm:w-auto left-0 sm:left-auto px-6 sm:px-0"
        style={ vw >= 1024 ? {
          right: "8%",
          bottom: "2rem",
          left: "auto",
          transform: `scale(${os})`,
          transformOrigin: "right bottom",
        } : vw >= 640 ? { 
          left: `${ox}%`, 
          top: `${oy}%`, 
          transform: `translate(-50%, -50%) scale(${os})` 
        } : {
          bottom: "calc(clamp(2rem, 5dvh, 3rem) + env(safe-area-inset-bottom, 0px))",
        }}
        data-testid="landing-hero-text-overlay"
      >
      <div className="text-center px-0 sm:px-6">
            {/* 1835 hidden on mobile per user request */}
            <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              {(isPrimaryAdmin || featureLocks.landing_become_member !== false) && (
              <Button className="rounded-full px-6 h-11 text-sm font-semibold bg-[hsl(var(--accent))] text-white hover:bg-[hsl(var(--accent))]/90" data-testid="hero-become-member-btn" onClick={() => setShowMemberApply(true)}>
                Become a member
              </Button>
              )}
              {(isPrimaryAdmin || featureLocks.landing_become_sponsor !== false) && (
              <Button variant="outline" className="rounded-full px-6 h-11 text-sm font-semibold border-white text-white hover:bg-white/10" data-testid="hero-become-sponsor-btn" onClick={() => setShowSponsorProposal(true)}>
                  Become a sponsor
              </Button>
              )}
            </div>
      </div>
      </div>
    </>
  );
}

// ─── Contact Form Section ─────────────────────────────────────────────
function ContactSection({ landing }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;
    setBusy(true);
    try {
      await api.post("/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
      });
      setSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Show inline error via toast
      const { toast } = await import("sonner");
      toast.error(detail || "Could not send your message. Please try again.");
    }
    setBusy(false);
  };

  const contactEmail = landing?.contact_email || "derwentcc@gmail.com";
  const contactPhone = landing?.contact_phone || "";

  return (
    <section
      className="py-12 sm:py-16 border-t border-border bg-[hsl(var(--secondary))]/40"
      data-testid="landing-contact-section"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-2 lg:gap-16 items-start">
          {/* Left — heading + info */}
          <div className="space-y-5">
            <div>
              <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">Get in touch</span>
              <h2 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight">
                Contact <span className="text-[hsl(var(--accent))]">Derwent CC</span>
              </h2>
              <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-md leading-relaxed">
                Have a question about membership or just want to say hello? Drop us a message and we'll get back to you.
              </p>
            </div>
            <div className="space-y-3">
              <a
                href={`mailto:${contactEmail}`}
                className="flex items-center gap-3 text-sm text-foreground hover:text-[hsl(var(--accent))] transition-colors group"
                data-testid="contact-email-link"
              >
                <div className="h-10 w-10 rounded-xl bg-[hsl(var(--accent))]/10 flex items-center justify-center shrink-0 group-hover:bg-[hsl(var(--accent))]/20 transition-colors">
                  <Mail className="h-4 w-4 text-[hsl(var(--accent))]" />
                </div>
                {contactEmail}
              </a>
              {contactPhone && (
                <a
                  href={`tel:${contactPhone}`}
                  className="flex items-center gap-3 text-sm text-foreground hover:text-[hsl(var(--accent))] transition-colors group"
                  data-testid="contact-phone-link"
                >
                  <div className="h-10 w-10 rounded-xl bg-[hsl(var(--accent))]/10 flex items-center justify-center shrink-0 group-hover:bg-[hsl(var(--accent))]/20 transition-colors">
                    <Phone className="h-4 w-4 text-[hsl(var(--accent))]" />
                  </div>
                  {contactPhone}
                </a>
              )}
              {landing?.location && (
                <div className="flex items-center gap-3 text-sm text-muted-foreground" data-testid="contact-location">
                  <div className="h-10 w-10 rounded-xl bg-[hsl(var(--accent))]/10 flex items-center justify-center shrink-0">
                    <MapPin className="h-4 w-4 text-[hsl(var(--accent))]" />
                  </div>
                  {landing.location}
                </div>
              )}
            </div>
          </div>

          {/* Right — form or success state */}
          <Card className="p-5 sm:p-6 rounded-2xl" data-testid="contact-form-card">
            {sent ? (
              <div className="flex flex-col items-center justify-center text-center py-8 gap-4" data-testid="contact-success">
                <div className="h-14 w-14 rounded-full bg-emerald-500/10 flex items-center justify-center">
                  <Check className="h-7 w-7 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold tracking-tight">Message sent!</h3>
                  <p className="text-sm text-muted-foreground mt-1 max-w-xs mx-auto">
                    Thanks for reaching out. We'll get back to you as soon as possible.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-full mt-2"
                  onClick={() => { setSent(false); setForm({ name: "", email: "", phone: "", message: "" }); }}
                  data-testid="contact-send-another"
                >
                  Send another message
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="contact-name" className="text-sm font-medium">Name</Label>
                    <Input
                      id="contact-name"
                      placeholder="Your full name"
                      value={form.name}
                      onChange={set("name")}
                      required
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="contact-email" className="text-sm font-medium">Email</Label>
                    <Input
                      id="contact-email"
                      type="email"
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={set("email")}
                      required
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="contact-phone" className="text-sm font-medium">
                    Phone <span className="text-muted-foreground font-normal">(optional)</span>
                  </Label>
                  <Input
                    id="contact-phone"
                    type="tel"
                    placeholder="04XX XXX XXX"
                    value={form.phone}
                    onChange={set("phone")}
                    data-testid="contact-phone-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="contact-message" className="text-sm font-medium">Message</Label>
                  <Textarea
                    id="contact-message"
                    placeholder="How can we help?"
                    rows={4}
                    value={form.message}
                    onChange={set("message")}
                    required
                    className="resize-none"
                    data-testid="contact-message-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={busy || !form.name.trim() || !form.email.trim() || !form.message.trim()}
                  className="w-full sm:w-auto h-11 rounded-full bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white px-8"
                  data-testid="contact-submit-btn"
                >
                  {busy ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
                  ) : (
                    <><Send className="h-4 w-4 mr-2" /> Send message</>
                  )}
                </Button>
              </form>
            )}
          </Card>
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const [landing, setLanding] = useState(null);
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [news, setNews] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [selectedSponsorId, setSelectedSponsorId] = useState(null);
  const eventsScrollRef = useRef(null);
  const ctaScrollRef = useRef(null);
  // Live section-height override from editor drag (transient, not persisted)
  const [liveHeights, setLiveHeights] = useState(null);
  // Live card-scale override from editor drag (transient, not persisted)
  const [liveCardScales, setLiveCardScales] = useState(null);
  // Editor can pause the sponsor marquee for reliable card overlay alignment
  const [marqueePaused, setMarqueePaused] = useState(false);
  // Mobile CTA rail navigation — track which button is currently in view
  // so we can disable the prev/next arrows at the edges and swap between
  // buttons via explicit tap targets (in addition to swipe).
  const [ctaIndex, setCtaIndex] = useState(0);
  const [showMemberApply, setShowMemberApply] = useState(false);
  const [showFollow, setShowFollow] = useState(false);
  const [showSponsorProposal, setShowSponsorProposal] = useState(false);
  const [featureLocks, setFeatureLocks] = useState({});
  const { user } = useAuth();
  const isPrimaryAdmin = user?.role === "primary_admin";
  const CTA_COUNT = 4;
  useEffect(() => {
    const el = ctaScrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const w = el.clientWidth || 1;
      const idx = Math.round(el.scrollLeft / w);
      setCtaIndex(Math.max(0, Math.min(CTA_COUNT - 1, idx)));
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);
  const scrollCta = (dir) => {
    const el = ctaScrollRef.current;
    if (!el) return;
    const w = el.clientWidth || 0;
    el.scrollBy({ left: dir === "left" ? -w : w, behavior: "smooth" });
  };

  // Lock body scroll when sponsor overlay is open (desktop only)
  useEffect(() => {
    if (selectedSponsorId && window.innerWidth >= 640) {
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = ""; };
    }
  }, [selectedSponsorId]);

  // Desktop hero logo scroll fade — gradual easeOut over full hero height
  const [logoOpacity, setLogoOpacity] = useState(0.95);
  useEffect(() => {
    if (window.innerWidth < 640) return;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const heroEl = document.querySelector('[data-landing-section="hero"]');
        if (!heroEl) { ticking = false; return; }
        const heroBottom = heroEl.offsetTop + heroEl.offsetHeight;
        // Spread the fade across the full hero + a bit beyond so it's very gradual
        const t = Math.min(1, Math.max(0, window.scrollY / (heroBottom * 1.1)));
        // easeOutCubic curve — fast at start (barely noticeable), slows to zero
        const eased = 1 - Math.pow(1 - t, 3);
        setLogoOpacity(0.95 * (1 - eased));
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Events rail edge tracking — mirrors the CTA pattern so the side
  // chevrons can auto-disable when there's nothing further to reveal in
  // either direction. Runs on scroll + on mount + on window resize +
  // whenever the events array changes (so freshly-loaded events flip the
  // "can scroll right" flag correctly).
  const [eventsCanLeft, setEventsCanLeft] = useState(false);
  const [eventsCanRight, setEventsCanRight] = useState(false);
  useEffect(() => {
    const el = eventsScrollRef.current;
    if (!el) return;
    const sync = () => {
      const maxScroll = el.scrollWidth - el.clientWidth;
      setEventsCanLeft(el.scrollLeft > 2);
      setEventsCanRight(el.scrollLeft < maxScroll - 2);
    };
    sync();
    el.addEventListener("scroll", sync, { passive: true });
    window.addEventListener("resize", sync);
    return () => {
      el.removeEventListener("scroll", sync);
      window.removeEventListener("resize", sync);
    };
  }, [events.length]);

  useEffect(() => {
    let cancelled = false;

    // One-shot load of everything on mount — hero copy, stats,
    // events, news, sponsors. Wrapped so the polling effect below can
    // reuse the same axios calls without duplicating them.
    (async () => {
      try {
        const [l, s, ev, nw, sp, fl] = await Promise.all([
          api.get("/landing"),
          api.get("/public/stats/summary").catch(() => null),
          api.get("/events/public/landing").catch(() => ({ data: [] })),
          api.get("/news/public/landing").catch(() => ({ data: [] })),
          api.get("/sponsors/public").catch(() => ({ data: [] })),
          api.get("/feature-locks").catch(() => ({ data: {} })),
        ]);
        if (cancelled) return;
        setLanding(l.data);
        setStats(s?.data || null);
        setEvents(ev?.data || []);
        setNews(nw?.data || []);
        setSponsors(sp?.data || []);
        setFeatureLocks(fl?.data || {});
      } catch (e) { console.error(e); }
    })();

    return () => { cancelled = true; };
  }, []);

  // Silently re-poll the "Upcoming events" strip every 2 minutes so a
  // newly-added event pops in without a hard reload. We only fire the
  // request when the tab is actually visible (saves API + battery), and
  // we refresh immediately whenever the tab regains focus after being
  // hidden so returning users always see fresh data. No loader / spinner
  // is shown — the React key on each card lets the DOM diff cleanly, so
  // the scroll position and any in-progress animations stay put.
  useEffect(() => {
    const REFRESH_MS = 2 * 60 * 1000; // 2 minutes

    const refreshEvents = async () => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      try {
        const { data } = await api.get("/events/public/landing");
        setEvents((prev) => {
          const next = Array.isArray(data) ? data : [];
          // Skip the state update when the payload is identical so we
          // don't trigger useless re-renders / scroll snap resets.
          if (prev.length === next.length && prev.every((p, i) => p?.id === next[i]?.id)) {
            return prev;
          }
          return next;
        });
      } catch (e) {
        // Silent — a transient network blip shouldn't spam the console
        // for a background poll.
      }
    };

    const id = setInterval(refreshEvents, REFRESH_MS);
    const onVisibility = () => {
      if (document.visibilityState === "visible") refreshEvents();
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const scrollEvents = (dir) => {
    const el = eventsScrollRef.current;
    if (!el) return;
    const step = Math.max(280, Math.floor(el.clientWidth * 0.85));
    el.scrollBy({ left: dir === "left" ? -step : step, behavior: "smooth" });
  };

  // Listen for live section-height and card-scale updates from editor iframe parent
  useEffect(() => {
    const handler = (e) => {
      if (e.origin !== window.location.origin) return;
      if (e.data?.type === "section-height-preview") {
        setLiveHeights(e.data.heights);
      }
      if (e.data?.type === "section-height-clear") {
        setLiveHeights(null);
      }
      if (e.data?.type === "card-scale-preview") {
        setLiveCardScales(e.data.card_scales);
      }
      if (e.data?.type === "card-scale-clear") {
        setLiveCardScales(null);
      }
      if (e.data?.type === "marquee-pause") {
        setMarqueePaused(true);
      }
      if (e.data?.type === "marquee-resume") {
        setMarqueePaused(false);
      }
      // Live hero image position/zoom from editor phone preview drag
      if (e.data?.type === "hero-image-preview") {
        setLanding((prev) => prev ? {
          ...prev,
          hero_image_x: e.data.x ?? prev.hero_image_x,
          hero_image_y: e.data.y ?? prev.hero_image_y,
          hero_image_scale: e.data.scale ?? prev.hero_image_scale,
        } : prev);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  // Helper: get effective section height (live override > saved > auto)
  const getSectionHeight = (sectionId) => {
    const live = liveHeights?.[sectionId];
    if (live != null && live > 0) return live;
    const saved = landing?.section_heights?.[sectionId];
    if (saved != null && saved > 0) return saved;
    return null; // auto
  };

  // Helper: get effective card scale for a given card key (live override > saved > default)
  const getCardScale = (cardKey) => {
    const live = liveCardScales?.[cardKey];
    if (live) return live;
    const saved = landing?.card_scales?.[cardKey];
    if (saved) return saved;
    return null; // use default dimensions
  };

  const hasStats = stats && stats.matches_played > 0;

  return (
    <PublicLayout>
      {/* ============ DYNAMIC SECTIONS (order from admin) ============ */}
      {/* Member apply dialog — centred card overlay */}
      <MemberApplyDialog open={showMemberApply} onClose={() => setShowMemberApply(false)} />
      <FollowDialog open={showFollow} onClose={() => setShowFollow(false)} />
      <SponsorProposalDialog open={showSponsorProposal} onClose={() => setShowSponsorProposal(false)} />
      {(landing?.section_order || ["hero", "sponsors", "events", "news"]).map((sectionId) => {
        switch (sectionId) {

          case "hero": {
            const heroH = getSectionHeight("hero");
            return (
              <div key="hero" data-landing-section="hero" className="sticky top-16 z-20 sm:static overflow-hidden" style={heroH ? { height: heroH, overflow: "hidden" } : {}}>
              <section
                style={heroH ? { height: "100%" } : {}}
                className="relative w-full overflow-hidden bg-[hsl(var(--secondary))] h-[clamp(50dvh,calc(100dvh_-_clamp(8rem,22dvh,13rem)),100dvh)] sm:h-auto sm:aspect-[16/9] md:aspect-[1920/660]"
                data-testid="landing-hero-image-section"
              >
                {landing?.hero_image_url && (
                  <HeroImage landing={landing} />
                )}
                {/* Dark gradient overlay for mobile text readability */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent sm:hidden z-[5] pointer-events-none" />
                {/* Desktop: subtle bottom gradient for button readability */}
                <div className="absolute inset-x-0 bottom-0 h-[40%] bg-gradient-to-t from-black/50 to-transparent hidden sm:block z-[5] pointer-events-none" />
                {/* Club logo on hero — visible on all screen sizes */}
                {/* Hero overlay — responsive: uses phone/tablet/desktop overlay values */}
                <HeroOverlay landing={landing} setShowMemberApply={setShowMemberApply} setShowSponsorProposal={setShowSponsorProposal} featureLocks={featureLocks} isPrimaryAdmin={isPrimaryAdmin} />
              </section>
              </div>
            );
          }

          case "sponsors": {
            const sponsorH = getSectionHeight("sponsors");
            return (
              <div key="sponsors" data-landing-section="sponsors" className="relative" style={sponsorH ? { height: sponsorH, overflow: "visible" } : {}}>
                <DccDuck height={80} />
                <SponsorShowcase sponsors={sponsors} selectedId={selectedSponsorId} setSelectedId={setSelectedSponsorId} landing={landing} getCardScale={getCardScale} marqueePaused={marqueePaused} />
              </div>
            );
          }

          case "events": {
            const eventsH = getSectionHeight("events");
            return events.length > 0 && (landing?.events_section_visible !== false) ? (
              <section key="events" data-landing-section="events" className="border-b border-border overflow-x-hidden overflow-y-visible" data-testid="landing-events-section" style={eventsH ? { height: eventsH } : {}}>
                <div className="mx-auto pt-0 pb-[10px] w-full max-w-[1920px]">
                  <div className="flex items-end justify-between gap-4 mb-2 sm:mb-[20px] flex-wrap pl-5 sm:pl-6 lg:pl-[232px] pr-5 sm:pr-6 lg:pr-[232px] py-1.5 sm:py-3 z-10" style={{ background: "linear-gradient(135deg, #1E3A8A 0%, #2749C9 55%, #4169E1 100%)" }}>
                    <div>
                      <h2
                        className="text-2xl sm:text-5xl lg:text-6xl tracking-tight uppercase leading-none text-white"
                        style={{ fontFamily: "'Barlow Condensed', 'Inter', sans-serif", fontWeight: 900 }}
                      >
                        {landing?.events_section_heading || "Upcoming events"}
                      </h2>
                    </div>
                    <Link
                      to="/events"
                      className="inline-flex items-center gap-1.5 text-white text-sm font-semibold hover:text-white/80 active:scale-95 transition-all mb-1"
                      data-testid="landing-all-events-btn"
                    >
                      All Events
                      <ArrowUpRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                  <div className="relative mx-5 sm:mx-6 lg:mx-[232px] overflow-hidden">
                      <div
                        ref={eventsScrollRef}
                        className="flex gap-2 sm:gap-5 overflow-x-auto scroll-smooth snap-x snap-mandatory pb-3 scrollbar-hide"
                        data-testid="landing-events-scroller"
                      >
                        {events.slice(0, landing?.events_max_display || 10).map((ev, idx) => (
                          <LandingEventCard key={ev.id} ev={ev} onOpen={setSelectedEvent}
                            cardKey={`events_${idx}`}
                            scaleOverride={getCardScale(`events_${idx}`)}
                          />
                        ))}
                      </div>
                      {eventsCanLeft && (
                        <button type="button" onClick={() => scrollEvents("left")} aria-label="Scroll events left"
                          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 inline-flex h-14 w-11 items-center justify-center text-[hsl(var(--fg))] bg-gradient-to-r from-white/95 via-white/70 to-transparent hover:from-white active:scale-95 transition-all"
                          data-testid="landing-events-scroll-left">
                          <ChevronLeft className="h-8 w-8" strokeWidth={3} />
                        </button>
                      )}
                      {eventsCanRight && (
                        <button type="button" onClick={() => scrollEvents("right")} aria-label="Scroll events right"
                          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 inline-flex h-14 w-11 items-center justify-center text-[hsl(var(--fg))] bg-gradient-to-l from-white/95 via-white/70 to-transparent hover:from-white active:scale-95 transition-all"
                          data-testid="landing-events-scroll-right">
                          <ChevronRight className="h-8 w-8" strokeWidth={3} />
                        </button>
                      )}
                  </div>
                </div>
              </section>
            ) : null;
          }

          case "news": {
            const newsH = getSectionHeight("news");
            return news.length > 0 && (landing?.news_section_visible !== false) ? (
              <section key="news" data-landing-section="news" className="border-b border-border" data-testid="landing-news-section" style={{ minHeight: newsH || undefined, overflow: "visible" }}>
                <div className="mx-auto pt-0 pb-[10px] px-5 sm:px-6 lg:px-0 w-full max-w-[1920px]">
                  <div className="flex items-end justify-between gap-4 mb-2 sm:mb-[20px] flex-wrap py-1.5 sm:py-3 pl-5 sm:pl-6 lg:pl-[50px] pr-5 sm:pr-6 lg:pr-[232px] -mx-5 sm:-mx-6 lg:ml-[182px] lg:mr-0 z-10" style={{ background: "linear-gradient(135deg, #1E3A8A 0%, #2749C9 55%, #4169E1 100%)" }}>
                    <h2
                      className="text-2xl sm:text-5xl lg:text-6xl tracking-tight uppercase leading-none text-white"
                      style={{ fontFamily: "'Barlow Condensed', 'Inter', sans-serif", fontWeight: 900 }}
                    >
                      {landing?.news_section_heading || "Latest news"}
                    </h2>
                    <Link
                      to="/news"
                      className="inline-flex items-center gap-1.5 text-white text-sm font-semibold hover:text-white/80 active:scale-95 transition-all mb-1"
                      data-testid="landing-all-news-btn"
                    >
                      All News
                      <ArrowUpRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                  <NewsMosaic items={news.slice(0, landing?.news_max_display || 8)} firstEvent={events[0] || null} onOpenEvent={setSelectedEvent} getCardScale={getCardScale} landing={landing} />
                </div>
              </section>
            ) : null;
          }

          default:
            return null;
        }
      })}

      {/* Event detail dialog — opened by clicking a card in the strip */}
      <EventDetailDialog ev={selectedEvent} onClose={() => setSelectedEvent(null)} landing={landing} />

      {/* ============ STATS LEADERBOARD ============ */}
      <StatsBoard />

      {/* ============ PLAYHQ PREVIEW (public) ============ */}
      <PlayHQPreview />

      {/* ============ SEASON STATS (public leaderboards) ============ */}
      {hasStats && (
        <section className="border-b border-border bg-[hsl(var(--secondary))]/30" data-testid="landing-stats-section">
          <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8 py-14 sm:py-20">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Season on the pitch</div>
                <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight">
                  {stats.season} <span className="font-serif-display text-[hsl(var(--accent))]">leaderboards</span>
                </h2>
                <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
                  Live season snapshot — top run-scorers, wicket-takers and the club's overall record.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <StatChip label="Played" value={stats.matches_played} testid="stat-chip-played" />
                <StatChip label="Won" value={stats.record.wins} tone="success" testid="stat-chip-won" />
                <StatChip label="Lost" value={stats.record.losses} tone="danger" testid="stat-chip-lost" />
                {stats.record.draws > 0 && <StatChip label="Drawn" value={stats.record.draws} testid="stat-chip-drawn" />}
                {stats.record.ties > 0 && <StatChip label="Tied" value={stats.record.ties} testid="stat-chip-tied" />}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Top batters */}
              <Card className="rounded-2xl overflow-hidden" data-testid="landing-top-batters">
                <div className="p-5 border-b border-border flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-[hsl(var(--accent))]/15 flex items-center justify-center">
                    <Trophy className="h-4 w-4 text-[hsl(var(--accent))]" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">Top Run-Scorers</div>
                    <div className="text-xs text-muted-foreground">Ranked by runs</div>
                  </div>
                </div>
                {stats.top_batters.length === 0 ? (
                  <div className="p-10 text-center text-sm text-muted-foreground">No batting stats yet.</div>
                ) : (
                  <ul>
                    {stats.top_batters.map((b, i) => (
                      <li
                        key={b.player_name + i}
                        className="flex items-center gap-4 px-5 py-3 border-b border-border/60 last:border-b-0"
                        data-testid={`landing-top-batter-${i}`}
                      >
                        <span className="w-6 text-center text-xs font-mono text-muted-foreground">{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold truncate">{b.player_name}</div>
                          <div className="text-[11px] text-muted-foreground">
                            {b.innings} inn · HS {b.highest} · Avg {b.average ?? "—"} · NO {b.not_outs || 0}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold tabular-nums">{b.runs}</div>
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">runs</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>

              {/* Top bowlers */}
              <Card className="rounded-2xl overflow-hidden" data-testid="landing-top-bowlers">
                <div className="p-5 border-b border-border flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-[hsl(var(--fg))]/10 flex items-center justify-center">
                    <Star className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold">Top Wicket-Takers</div>
                    <div className="text-xs text-muted-foreground">Ranked by wickets</div>
                  </div>
                </div>
                {stats.top_bowlers.length === 0 ? (
                  <div className="p-10 text-center text-sm text-muted-foreground">No bowling stats yet.</div>
                ) : (
                  <ul>
                    {stats.top_bowlers.map((b, i) => (
                      <li
                        key={b.player_name + i}
                        className="flex items-center gap-4 px-5 py-3 border-b border-border/60 last:border-b-0"
                        data-testid={`landing-top-bowler-${i}`}
                      >
                        <span className="w-6 text-center text-xs font-mono text-muted-foreground">{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold truncate">{b.player_name}</div>
                          <div className="text-[11px] text-muted-foreground">
                            {b.innings} inn · {Number(b.overs || 0).toFixed(1)} ov · Avg {b.average ?? "—"} · Econ {b.economy ?? "—"}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold tabular-nums">{b.wickets}</div>
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">wkts</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </div>

            <div className="mt-8 text-center">
              <Link to="/login" className="text-sm font-medium inline-flex items-center gap-1.5 underline-offset-4 hover:underline text-[hsl(var(--accent))]" data-testid="landing-stats-members-link">
                Members see the full stats page <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ============ CONTACT FORM ============ */}
      <ContactSection landing={landing} />

      {/* ============ BIG CTA ============ */}
      <section className="bg-[hsl(var(--fg))] text-[hsl(var(--bg))] min-h-[250px] py-10 sm:py-0 sm:h-[250px]">
        <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8 h-full flex flex-col justify-center gap-5">
          <div className="flex flex-col items-center text-center">
              <div className="text-xs uppercase tracking-[0.18em] opacity-60">Get involved</div>
              <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight text-balance">
              Ready to <span className="text-[hsl(var(--accent))]">play</span>, watch, or just stay in the loop?
            </h2>
          </div>
          <div className="grid grid-cols-2 gap-3 w-full max-w-sm sm:max-w-none sm:flex sm:flex-row sm:flex-wrap sm:justify-center mx-auto">
            {(isPrimaryAdmin || featureLocks.landing_apply_cta !== false) && (
            <Button size="lg" className="h-12 w-full sm:w-48 btn-accent-modern" data-testid="cta-apply-button" onClick={() => setShowMemberApply(true)}>
                Become a member
            </Button>
            )}
            {(isPrimaryAdmin || featureLocks.landing_sponsor_cta !== false) && (
            <Button size="lg" variant="outline" className="h-12 w-full sm:w-48 rounded-full bg-transparent text-[hsl(var(--bg))] border-white/20 hover:bg-white/10 hover:text-[hsl(var(--bg))]" data-testid="cta-sponsor-button" onClick={() => setShowSponsorProposal(true)}>
                Become a sponsor
            </Button>
            )}
            {(isPrimaryAdmin || featureLocks.landing_follow_club !== false) && (
            <Button size="lg" variant="outline" className="h-12 w-full sm:w-48 rounded-full bg-transparent text-[hsl(var(--bg))] border-white/20 hover:bg-white/10 hover:text-[hsl(var(--bg))]" data-testid="cta-follow-button" onClick={() => setShowFollow(true)}>
                Follow the club
            </Button>
            )}
          </div>
        </div>
      </section>

      {/* ============ AFFILIATIONS FOOTER ============ */}
      {landing?.affiliations?.length > 0 && (
        <section className="py-8 bg-transparent" data-testid="landing-affiliations-footer">
          <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
            <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-muted-foreground/50 mb-5 text-center">Affiliated With</div>
            <div className="flex items-center justify-center gap-8 sm:gap-12 flex-wrap">
              {landing.affiliations.map((aff, idx) => {
                const affKey = aff.name || `aff-${idx}`;
                const inner = (
                  <div className="flex items-center justify-center h-14 sm:h-16 min-w-[44px] px-2 opacity-60 hover:opacity-100 transition-opacity" data-testid={`affiliation-footer-${idx}`}>
                    {aff.logo_url ? (
                      <img src={aff.logo_url} alt={aff.name || ""} className="h-full w-auto max-w-[120px] object-contain" />
                    ) : (
                      <span className="text-xs sm:text-sm font-bold tracking-tight text-muted-foreground">{aff.name}</span>
                    )}
                  </div>
                );
                return aff.website_url ? (
                  <a key={affKey} href={aff.website_url} target="_blank" rel="noopener noreferrer" title={aff.name}>
                    {inner}
                  </a>
                ) : (
                  <div key={affKey} title={aff.name}>{inner}</div>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </PublicLayout>
  );
}
